/**
 * build-routes.ts - claim-aware route registration for the unified SPA.
 *
 * Pure function buildRoutes(user) returns the React Router data router
 * RouteObject[] for the current authenticated user. Per ADR-006 section 1
 * the SPA registers different route trees based on JWT claims:
 *
 *   | actor_type     | impersonating_as | Routes                    | Default landing |
 *   |----------------|------------------|---------------------------|-----------------|
 *   | (unauthenticated)               | login + 404               | n/a             |
 *   | internal_staff | false            | admin (lazy) + client + 404 | /admin/fleet  |
 *   | internal_staff | true             | client routes             | /now            |
 *   | client_user    | (any)            | client routes             | /now            |
 *
 * Phase 4 amendment 2026-04-27 (per ADR-006 amendment same date):
 *   internal_staff with impersonating_as=false now ALSO registers /now and
 *   /event/:id so admins can click the NOW item in the top nav and see the
 *   live concentric-zones viz. Default landing stays /admin/fleet; the
 *   client routes are an additional surface area, not a default destination.
 *   The Phase 3.1 impersonation runtime stays as dormant dead code per the
 *   ADR amendment (no behavior depends on impersonating_as=true anymore).
 *
 * A client_user JWT does NOT register /admin/* routes; visiting /admin/fleet
 * yields the SPA 404 and the lazy admin chunk does not download.
 *
 * Lazy admin chunks:
 *   The four admin route components (FleetOverview / SitesPage /
 *   SiteDetailPage / AlertsPage) are loaded via React.lazy at the module
 *   top level so Vite emits them as separate chunks. vite.config.ts also
 *   has an unconditional manualChunks rule routing src/features/admin/**
 *   to a chunk named "admin"; together these guarantee a deterministic
 *   admin chunk for the bundle-isolation guard.
 *
 * Phase 4 Plan 04 extension:
 *   buildRoutes now registers all four Phase 4 admin route slots upfront
 *   (/admin/fleet, /admin/sites, /admin/sites/:siteId, /admin/alerts).
 *   Plans 04-05/06/07/08 only REPLACE the lazy-loaded module FILES and do
 *   NOT edit this file, eliminating Wave 3 conflicts on build-routes.tsx.
 *
 * Auth loader:
 *   Both branches attach requireAuthLoader to the AppShell layout route so
 *   any direct visit without a token bounces to /login?returnTo=<path>.
 *
 * Modified: 2026-04-26
 */
import { lazy, Suspense } from 'react';
import { Navigate, redirect } from 'react-router-dom';
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { AppShell } from '@/features/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { NowPage } from '@/pages/NowPage';
import { EventPage } from '@/pages/EventPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { TOKEN_KEYS } from '@/lib/api-client';
import type { User } from '@/features/auth/schemas';
// 2026-06-10 (UI/UX pass): shared page-shaped skeleton for every lazy route
// boundary below. Replaces the previous `fallback={null}` blank viewport so
// a cold-cache chunk download telegraphs "page is coming" in the skeleton
// system's visual language. Entry-chunk-resident by design (the fallback
// must render before any lazy chunk exists); composes only the
// entry-friendly Skeleton component, so bundle isolation is unaffected.
import { RouteFallback } from '@/components/RouteFallback';

/**
 * Lazy-loaded /admin/fleet content. Vite splits this into a separate chunk;
 * the entry chunk has no eager reference to the FleetOverview module body.
 * The dynamic import path is matched by the manualChunks rule in
 * vite.config.ts so the chunk filename starts with "admin-".
 *
 * FleetOverview re-exports AdminFleetPlaceholder in Phase 4 Plan 04; Plan
 * 04-05 replaces the body with the real fleet KPI strip + alerts ticker
 * + recent activity feed.
 */
const FleetOverview = lazy(
  () => import('@/features/admin/FleetOverview'),
);

/**
 * Lazy-loaded /admin/sites table. Phase 4 Plan 04 ships a minimal
 * placeholder; Plan 04-06 replaces the body with the cross-org sortable
 * sites table (filters + pagination + WebSocket heartbeat updates).
 */
const SitesPage = lazy(
  () => import('@/features/admin/SitesPage'),
);

/**
 * Lazy-loaded /admin/sites/:siteId/:tab? detail page. Plan 04-07 originally
 * shipped this as a Radix Dialog overlay drawer; the drawer-to-page
 * conversion plan (A of 4) replaced the overlay with a dedicated full-page
 * route. Plan D1 of 4 then promoted tab state from a ?tab= query string to
 * a URL path segment so each tab has a canonical, shareable URL:
 *
 *   /admin/sites/:siteId               -> redirects to /:siteId/overview
 *   /admin/sites/:siteId/overview      -> Overview tab (default landing)
 *   /admin/sites/:siteId/cameras       -> Cameras tab
 *   /admin/sites/:siteId/:tab          -> any registered tab (live, sensors,
 *                                         activity, zones, history, config)
 *
 * The optional :tab? param means a single route slot still serves a bare
 * /admin/sites/:siteId hit; SiteDetailPage reads useParams<{siteId, tab?}>()
 * directly (no nested children, no <Outlet />) and handles the legacy
 * ?tab= query param via an in-component redirect for backwards compat.
 */
const SiteDetailPage = lazy(
  () => import('@/features/admin/SiteDetailPage'),
);

/**
 * Lazy-loaded /admin/alerts inbox. Phase 4 Plan 04 ships a minimal
 * placeholder; Plan 04-08 replaces the body with the fleet-wide alerts
 * table (severity filters + site filter + ack + bulk-ack + evidence
 * drill-in via RSSI waveform + camera clip).
 */
const AlertsPage = lazy(
  () => import('@/features/admin/AlertsPage'),
);

/**
 * Lazy-loaded /admin/recordings discovery surface (Phase 10 follow-up
 * 2026-05-01; renamed in Phase 19 Plan 19-04 / CONS-04 on 2026-05-22
 * from /admin/replays). Lists alerts + recording sessions that have a
 * replay bundle, filterable by site / chip / severity / time range.
 * Lives under src/features/admin so vite manualChunks routes it into
 * the admin chunk. The /admin/replays route is preserved as a
 * Navigate redirect to /admin/recordings (registered below) for ~1
 * release so externally-shared bookmarks still land users on the new
 * URL.
 */
const RecordingsPage = lazy(
  () => import('@/features/admin/RecordingsPage'),
);

/**
 * Lazy-loaded /admin/locations page (Phase 12 Plan 12-06).
 *
 * CRUD surface for the Location entity introduced by Plan 12-02: list,
 * create, edit, delete locations under an organization. Sentries assigned
 * to a location appear in the table's "Sentries" column; deleting a
 * location unassigns (but does not delete) the assigned sentries via the
 * ON DELETE SET NULL FK on sites.location_id.
 *
 * Lives under src/features/admin/** so vite manualChunks routes it into
 * the `admin` chunk; the bundle-isolation guard ensures the SENTINEL
 * strings inside this module stay out of the entry chunk.
 */
const LocationsPage = lazy(
  () => import('@/features/admin/LocationsPage'),
);

/**
 * Lazy-loaded /admin/mobile-sessions "Mobile Uploads" review page
 * (re-activated 2026-05-29).
 *
 * Phase 19 / CONS-03 retired this route when mobile sessions were merged into
 * the unified Recordings table. Now that Recordings shows only ENRICHED mobile
 * sessions (fleet-api enriched_only - those with a real bundle), this preserved
 * page returns as the review surface for ALL pushed mobile recordings + their
 * pushed/pulled/enriched lifecycle. It reads GET /v1/admin/mobile-sessions,
 * which lists every session regardless of enrichment. Lives under
 * src/features/admin/** so vite manualChunks routes it into the admin chunk.
 */
const MobileSessionsPage = lazy(
  () => import('@/features/admin/MobileSessionsPage'),
);

/**
 * Lazy-loaded /admin/orgs page (Phase 15 Plan 15-12; UNI-10).
 *
 * SPA admin surface for organization CRUD list + creation. Backend
 * `POST /v1/admin/orgs` already exists (Phase 1.5 Plan 01.5-03); this
 * page ships the dialog that calls it plus a list of every org the
 * staff_admin can see.
 *
 * Lives under src/features/admin/** so vite manualChunks routes it into
 * the admin chunk; the lazy import boundary keeps it out of the entry
 * chunk for client users (who hit the SPA 404 if they visit the route).
 */
const OrganizationsPage = lazy(
  () => import('@/features/admin/OrganizationsPage'),
);

/**
 * Stub placeholder for /mobile-sessions/:sessionId/replay
 * (Phase 13 Wave 3 Plan 13-12 registered the route slot; Plan 13-13
 * ships the real MobileSessionReplayPage which is now lazy-imported
 * below). Kept around as a documentation-only artifact so the
 * historical wiring is discoverable; the route registrations below
 * no longer reference this stub.
 *
 * Exported so tsc --noEmit's noUnusedLocals does not trip; consumers
 * outside this module SHOULD NOT use this stub - the real route uses
 * the lazy-loaded MobileSessionReplayPage below.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function MobileSessionReplayStub(): JSX.Element {
  return (
    <main
      data-testid="mobile-session-replay-stub"
      style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--colors-text-secondary, #888)',
      }}
    >
      <h1
        style={{
          fontFamily:
            'var(--fonts-sans, -apple-system, BlinkMacSystemFont, sans-serif)',
          fontSize: '20px',
          margin: '0 0 8px',
        }}
      >
        Mobile session replay
      </h1>
      <p>
        The replay viewer lands in Plan 13-13. The session is bound and
        enrichment is queued.
      </p>
    </main>
  );
}

/**
 * Lazy-loaded /mobile-sessions/:sessionId/replay viewer (Phase 13 Wave
 * 3 Plan 13-13 / REP-16).
 *
 * The replay page composes narration + zone arcs + camera clips into
 * one viewer driven by a single playback clock. Lives OUTSIDE
 * src/features/admin/** so it can ship as its own lazy chunk (the
 * route is registered in BOTH staffRoutes() and clientRoutes() so
 * client users can view their own session replays). vite manualChunks
 * does NOT route src/features/mobile-session-replay/** into the admin
 * chunk; the dynamic import boundary below splits it into its own
 * dedicated chunk.
 */
const MobileSessionReplayPage = lazy(
  () => import('@/features/mobile-session-replay/MobileSessionReplayPage'),
);

/**
 * Lazy-loaded /alerts/:alertId/replay page (Phase 10 Plan 10-02).
 *
 * The alert-replay feature lives OUTSIDE `src/features/admin/**` so it
 * can be reached from BOTH the staff branch AND the client branch.
 * The lazy import boundary keeps it out of the entry chunk; vite's
 * manualChunks rule does not route it into `admin` either, so it ships
 * as its own dedicated chunk (named after the dynamic-import path).
 */
const AlertReplayPage = lazy(
  () => import('@/features/alert-replay/AlertReplayPage'),
);

/**
 * Lazy-loaded /admin/sentries/:siteId/live page (Phase 16 Plan 16-02; LIVE-02).
 *
 * The Live view shows a real-time scrolling RSSI chart, satellite-map zone
 * overlay driven by per-zone EMA over incoming WS detection frames, refreshing
 * camera snapshots, and a live alerts panel. Mirrors the replay UI's visual
 * vocabulary but consumes the WS firehose (site:{id}:detections + alerts
 * topics) instead of buffered window data. Sibling of ReplayPlayer, NOT a
 * source: 'live' branch.
 *
 * Lives OUTSIDE src/features/admin/** so vite manualChunks does NOT route it
 * into the admin chunk; the lazy boundary below splits it into its own
 * dedicated live-view-* chunk. Per RESEARCH.md §"Bundle size impact" (line
 * 548), the new chunk is small and the network round-trip is sub-100ms.
 *
 * Registered for BOTH /admin/sentries/:siteId/live AND
 * /admin/sites/:siteId/live (the Phase 12 alias contract). Both slots are
 * registered BEFORE the generic /admin/{sentries,sites}/:siteId/:tab? slots
 * so react-router's path matching resolves the more-specific /live segment
 * to LivePage; Phase 16 Plan 16-02 Task 3 ALSO removed 'live' from
 * SiteDetailPage's TAB_IDS as a belt-and-braces collision fix.
 */
const LivePage = lazy(() => import('@/features/live-view/LivePage'));

/**
 * Auth-required loader for protected layout routes.
 *
 * Reads the access token directly from localStorage (cheap, sync). If
 * absent, redirects to /login with a returnTo query param so the post-login
 * navigation can land back on the original target. Loaders run before any
 * React component renders; this is the fastest gate.
 *
 * @param args - Loader function arguments from react-router-dom.
 * @returns null if a token is present, or a redirect Response otherwise.
 */
function requireAuthLoader({ request }: LoaderFunctionArgs) {
  const token = localStorage.getItem(TOKEN_KEYS.access);

  if (!token) {
    const url = new URL(request.url);
    const returnTo = url.pathname + url.search;
    return redirect(`/login?returnTo=${encodeURIComponent(returnTo)}`);
  }

  return null;
}

/**
 * Returns the admin route tree (registered for internal_staff with
 * impersonating_as=false). Does NOT include client routes; a direct visit
 * to /now under this branch falls through to the 404 catch-all.
 *
 * Phase 4 Plan 04 extension: registers all four admin route slots upfront
 * (/admin/fleet, /admin/sites, /admin/sites/:siteId, /admin/alerts). Each
 * slot lazy-loads the canonical module; Plans 04-05..08 swap the module
 * file bodies in-place and never edit this function.
 */
function staffRoutes(): RouteObject[] {
  return [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      element: <AppShell />,
      loader: requireAuthLoader,
      children: [
        {
          path: '/',
          element: <Navigate to="/admin/fleet" replace />,
        },
        {
          // Suspense lives here (NOT in the entry chunk's component tree)
          // so the only top-level reference to each admin module is the
          // React.lazy boundary above. Fallback was null through 2026-06-10
          // (rationalized as sub-100ms on a warm LAN); it is now the shared
          // RouteFallback page skeleton so cold-cache or slow-link chunk
          // loads never show a dead blank viewport.
          path: '/admin/fleet',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <FleetOverview />
            </Suspense>
          ),
        },
        {
          path: '/admin/sites',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <SitesPage />
            </Suspense>
          ),
        },
        // Phase 16 Plan 16-02 (LIVE-02): Live view for a single sentry. Mounted
        // under BOTH /admin/sentries/:siteId/live AND /admin/sites/:siteId/live
        // so the Phase 12 alias contract holds. The page is staff-only;
        // client_user JWTs hit the 404 catch-all because clientRoutes() does
        // NOT register the route.
        //
        // ORDER MATTERS: these routes are registered BEFORE the generic
        // /admin/{sentries,sites}/:siteId/:tab? routes so react-router's path
        // matching resolves the more-specific /live segment to LivePage. Belt
        // and braces — Task 3 ALSO removes 'live' from SiteDetailPage's
        // TAB_IDS so even if the order is wrong, no SiteLiveTab will render.
        {
          path: '/admin/sites/:siteId/live',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <LivePage />
            </Suspense>
          ),
        },
        {
          // Site Detail uses :siteId (the canonical UUID path param) plus an
          // optional :tab? segment (Plan D1 of 4). Path forms:
          //   /admin/sites/:siteId         -> page redirects to /:siteId/overview
          //   /admin/sites/:siteId/:tab    -> renders the named tab body
          // The page is a single component that reads useParams<{siteId, tab?}>()
          // directly; we deliberately do NOT use nested children + <Outlet />
          // because the tabs share a common header/tablist chrome that lives
          // inside SiteDetailPage. Legacy ?tab= URLs are detected at mount and
          // redirected to the path-segment form for backwards compatibility.
          path: '/admin/sites/:siteId/:tab?',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <SiteDetailPage />
            </Suspense>
          ),
        },
        // ---------------------------------------------------------------
        // Phase 12 Plan 12-07 (2026-05-19): URL aliases for the
        // "Sentries" display rename. Both /admin/sentries and
        // /admin/sentries/:siteId/:tab? lazy-load the SAME SitesPage and
        // SiteDetailPage components as their /admin/sites/* counterparts;
        // the legacy /admin/sites/* paths keep working for back-compat
        // with bookmarks and externally-shared links. URL params stay
        // :siteId (NOT :sentryId) since DB column names, API paths, and
        // the underlying TypeScript types are all preserved by the
        // rename. SitesPage / SiteDetailPage / SitesTable components
        // continue to read useParams<{siteId, tab?}>() unchanged.
        // ---------------------------------------------------------------
        {
          path: '/admin/sentries',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <SitesPage />
            </Suspense>
          ),
        },
        // Phase 16 Plan 16-02 (LIVE-02): the /admin/sentries/* alias of the
        // /live route. Registered BEFORE the :tab? alias for the same
        // ordering reason called out above.
        {
          path: '/admin/sentries/:siteId/live',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <LivePage />
            </Suspense>
          ),
        },
        {
          path: '/admin/sentries/:siteId/:tab?',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <SiteDetailPage />
            </Suspense>
          ),
        },
        {
          path: '/admin/alerts',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <AlertsPage />
            </Suspense>
          ),
        },
        // Phase 10 follow-up 2026-05-01: replay-discovery surface.
        // Closes the gap surfaced when a user couldn't find which of
        // N alerts on a site actually have a playable replay. Lists
        // alerts (and recording sessions) that have a bundle,
        // filterable by site / chip / severity / time range. Each row
        // links to /alerts/{id}/replay (alert source) or
        // /mobile-sessions/{id}/replay (session source).
        //
        // Phase 19 / CONS-04 (2026-05-22): renamed from /admin/replays
        // to /admin/recordings. The legacy /admin/replays path stays
        // registered immediately below as a Navigate redirect for ~1
        // release so externally-shared bookmarks still land users on
        // the new URL.
        {
          path: '/admin/recordings',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <RecordingsPage />
            </Suspense>
          ),
        },
        {
          // Phase 19 / CONS-04 back-compat redirect. Navigate with
          // replace=true so the browser back button doesn't bounce
          // the user back to /admin/replays in a loop.
          path: '/admin/replays',
          element: <Navigate to="/admin/recordings" replace />,
        },
        // Phase 12 Plan 12-06: Locations admin CRUD page. Sits under
        // staffRoutes() so client_user JWTs that visit the route hit
        // the SPA 404 page; the admin chunk also stays out of the
        // entry chunk for client users via the lazy import boundary.
        {
          path: '/admin/locations',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <LocationsPage />
            </Suspense>
          ),
        },
        // Mobile Uploads review surface (re-activated 2026-05-29). Lists ALL
        // pushed mobile recordings + their pushed/pulled/enriched status so
        // raw uploads now excluded from the enriched-only Recordings table
        // stay reviewable. Path stays /admin/mobile-sessions (the page's own
        // internal links + synthetic-upload nav already target it); the TopNav
        // entry is labeled "Mobile Uploads". The /mobile-sessions/:sessionId/
        // replay route (further down) STAYS registered for Watch links.
        {
          path: '/admin/mobile-sessions',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <MobileSessionsPage />
            </Suspense>
          ),
        },
        // Phase 15 Plan 15-12 (UNI-10): Organizations admin surface.
        // staff_admin-gated CRUD list + create-organization dialog.
        // Sits under staffRoutes() so client_user JWTs that visit the
        // route hit the SPA 404 page; the admin chunk also stays out of
        // the entry chunk for client users via the lazy import boundary.
        {
          path: '/admin/orgs',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <OrganizationsPage />
            </Suspense>
          ),
        },
        // Phase 13 Wave 3 Plan 13-13: real MobileSessionReplayPage
        // (swapped in from Plan 13-12's stub). Lazy-loaded into its own
        // dedicated chunk so the entry chunk stays lean and the page
        // does not ride along into the admin chunk. The Plan 13-12
        // MobileSessionsTable row click navigation already targets
        // this route; the swap is regression-safe via the existing
        // test_row_click_navigates_to_replay test.
        {
          path: '/mobile-sessions/:sessionId/replay',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <MobileSessionReplayPage />
            </Suspense>
          ),
        },
        // Per ADR-006 amendment 2026-04-27: staff see client routes directly.
        // The NOW item in the top nav links to /now and entry-event clicks
        // navigate to /event/:id; without these registrations, staff would
        // fall through to the 404 catch-all.
        { path: '/now', element: <NowPage /> },
        { path: '/event/:id', element: <EventPage /> },
        // Phase 10 Plan 10-02: alert-replay page reachable from both
        // /admin/alerts (staff) and /now (client). Suspense fallback is
        // null because the chunk is small and round-trip is sub-100ms.
        {
          path: '/alerts/:alertId/replay',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <AlertReplayPage />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ];
}

/**
 * Returns the client route tree (registered for client_user OR internal_staff
 * with impersonating_as=true). Does NOT include admin routes; a direct visit
 * to /admin/fleet under this branch falls through to the 404 catch-all and
 * the lazy admin chunk never downloads.
 */
function clientRoutes(): RouteObject[] {
  return [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      element: <AppShell />,
      loader: requireAuthLoader,
      children: [
        {
          path: '/',
          element: <Navigate to="/now" replace />,
        },
        {
          path: '/now',
          element: <NowPage />,
        },
        {
          path: '/event/:id',
          element: <EventPage />,
        },
        // Phase 10 Plan 10-02: alert-replay page also registered for
        // client_user JWTs (and staff impersonating a client).
        {
          path: '/alerts/:alertId/replay',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <AlertReplayPage />
            </Suspense>
          ),
        },
        // Phase 13 Wave 3 Plan 13-13: real MobileSessionReplayPage
        // (swapped in from Plan 13-12's stub). Same lazy import as the
        // staff branch above; client roles can view their own session
        // replays. The cloud-side GET /v1/mobile/sessions/{id}/replay
        // endpoint enforces ownership via scope.owns(sess) so cross-
        // org users get 404 (no existence leak).
        {
          path: '/mobile-sessions/:sessionId/replay',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <MobileSessionReplayPage />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ];
}

/**
 * Returns the public-only route tree for an unauthenticated user.
 *
 * Only /login renders content; the layout AppShell still has the auth
 * loader attached so any /now or /admin/fleet hit bounces to /login
 * with a returnTo param. We register the AppShell branch with NO children
 * other than the redirect-to-/now so the loader can fire on root paths;
 * the catch-all handles everything else.
 */
function publicRoutes(): RouteObject[] {
  return [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      element: <AppShell />,
      loader: requireAuthLoader,
      children: [
        // The auth loader bounces any of these paths to /login when no
        // token is present. We register /now + /event/:id + /admin/fleet
        // as element-less children so a direct URL hit still routes through
        // the loader (rather than falling through to the 404 catch-all).
        {
          path: '/',
          element: <Navigate to="/now" replace />,
        },
        {
          path: '/now',
          element: <NowPage />,
        },
        {
          path: '/event/:id',
          element: <EventPage />,
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ];
}

/**
 * Build the route table for a given authenticated user (or null).
 *
 * This is a pure function: same input -> same output. Memoization belongs
 * one layer up, in router.tsx (createBrowserRouter must not be rebuilt
 * on every render).
 *
 * @param user - The current User object from useAuth(), or null if
 *   unauthenticated.
 * @returns RouteObject[] suitable for createBrowserRouter / createMemoryRouter.
 */
export function buildRoutes(user: User | null): RouteObject[] {
  if (user === null) {
    return publicRoutes();
  }

  // Staff with NO active impersonation: admin surface only.
  if (user.actor_type === 'internal_staff' && !user.impersonating_as) {
    return staffRoutes();
  }

  // Everyone else (client_user OR staff currently impersonating a client org)
  // sees the client route tree per ADR-006 section 1.
  return clientRoutes();
}
